/* eslint-disable max-len */
/* eslint-disable import-helpers/order-imports */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { NumericFormat } from 'react-number-format';
import {
  Alert,
  Button,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  UncontrolledTooltip,
} from 'reactstrap';
import * as Yup from 'yup';

import api from '../../services/api';
import { adjustValue, normalizeTextForSearch } from '../../utils/functions';
import { MoneyFormatter } from '../Formatters';

const TIPOS = {
  percentual: 'percentual',
  valorFixo: 'valorFixo',
  valoresDiretos: 'valoresDiretos',
};

const buildEdicaoDiretaSnapshot = ({
  tipoAlteracao,
  selectAll,
  selectedIds = [],
  valoresDiretos = {},
  datasVigenciaPorItem = {},
}) => JSON.stringify({
  tipoAlteracao,
  selectAll: Boolean(selectAll),
  selectedIds: [...selectedIds].map(String).sort(),
  valoresDiretos,
  datasVigenciaPorItem,
});

const toDatetimeLocalValue = (dateValue) => {
  if (!dateValue) return '';
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const datetimeLocalToISO = (value) => {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
};

export const computeProjectedValor = (valorAtual, especialidadeId, preview) => {
  if (!preview || !especialidadeId) return null;
  const {
    tipoAlteracao,
    percentual,
    valorFixo,
    valoresDiretos = {},
    selectedIds,
    selectAll,
  } = preview;

  const isSelected = selectAll || (selectedIds || []).includes(especialidadeId);
  if (!isSelected) return null;

  const atual = parseFloat(valorAtual) || 0;

  if (tipoAlteracao === TIPOS.percentual) {
    if (percentual === null || percentual === '' || percentual === undefined) return null;
    return adjustValue(atual, Number(percentual), 0);
  }

  if (tipoAlteracao === TIPOS.valorFixo) {
    if (valorFixo === null || valorFixo === '' || valorFixo === undefined) return null;
    return Math.round((atual + Number(valorFixo)) * 100) / 100;
  }

  if (tipoAlteracao === TIPOS.valoresDiretos) {
    if (valoresDiretos[especialidadeId] === undefined || valoresDiretos[especialidadeId] === '') {
      return null;
    }
    return Number(valoresDiretos[especialidadeId]);
  }

  return null;
};

function TabelaValorReajustePanel(
  {
    valores = [],
    tabelaId,
    tabelaTipo = 'especialista',
    disabled = false,
    onApplied = () => null,
    onRemoveValor = () => null,
    notificationRef,
    setLoading,
  },
  ref,
) {
  const [tipoAlteracao, setTipoAlteracao] = useState(TIPOS.percentual);
  const [percentual, setPercentual] = useState(null);
  const [valorFixo, setValorFixo] = useState(null);
  const [valoresDiretos, setValoresDiretos] = useState({});
  const [datasVigenciaPorItem, setDatasVigenciaPorItem] = useState({});
  const [dataVigencia, setDataVigencia] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [busca, setBusca] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [reajusteAlert404, setReajusteAlert404] = useState('');
  const [confirming, setConfirming] = useState(false);
  const edicaoDiretaBaselineRef = useRef(buildEdicaoDiretaSnapshot({
    tipoAlteracao: TIPOS.percentual,
    selectAll: true,
    selectedIds: [],
    valoresDiretos: {},
    datasVigenciaPorItem: {},
  }));

  const captureEdicaoDiretaBaseline = useCallback((snap) => {
    edicaoDiretaBaselineRef.current = buildEdicaoDiretaSnapshot(snap);
  }, []);

  const hasTabelaId = Boolean(tabelaId);
  const idParamName = tabelaTipo === 'cliente'
    ? 'tabelaValoresClienteId'
    : 'tabelaValoresEspecialistaId';

  const allIds = useMemo(
    () => valores.map((v) => v.especialidade?.value).filter(Boolean),
    [valores],
  );

  const filteredValores = useMemo(() => {
    const term = normalizeTextForSearch(busca);
    const sorted = [...valores].sort((a, b) => (
      a.especialidade?.label > b.especialidade?.label ? 1 : -1
    ));
    if (!term) return sorted;
    return sorted.filter((v) => normalizeTextForSearch(v.especialidade?.label).includes(term));
  }, [valores, busca]);

  const previewConfig = useMemo(() => ({
    tipoAlteracao,
    percentual,
    valorFixo,
    valoresDiretos,
    selectedIds: selectAll ? allIds : selectedIds,
    selectAll,
  }), [tipoAlteracao, percentual, valorFixo, valoresDiretos, selectedIds, selectAll, allIds]);

  const resetState = useCallback(() => {
    setTipoAlteracao(TIPOS.percentual);
    setPercentual(null);
    setValorFixo(null);
    setValoresDiretos({});
    setDatasVigenciaPorItem({});
    setDataVigencia('');
    setSelectedIds([]);
    setSelectAll(true);
    setBusca('');
    setValidationErrors({});
    setReajusteAlert404('');
    setConfirming(false);
    captureEdicaoDiretaBaseline({
      tipoAlteracao: TIPOS.percentual,
      selectAll: true,
      selectedIds: [],
      valoresDiretos: {},
      datasVigenciaPorItem: {},
    });
  }, [captureEdicaoDiretaBaseline]);

  const hydrateFromPending = useCallback((res) => {
    if (!res) {
      resetState();
      return;
    }

    const tipo = res.tipoAlteracao || TIPOS.percentual;
    setTipoAlteracao(tipo);
    setPercentual(res.percentual ?? null);
    setValorFixo(res.valorFixo ?? null);
    setDataVigencia(toDatetimeLocalValue(res.data));

    let nextSelectAll = true;
    let nextSelectedIds = [];
    let mapValores = {};
    let mapDatas = {};

    const ids = (res.especialidadeIds || []).map((id) => id?.toString?.() ?? String(id));
    if (!ids.length && tipo !== TIPOS.valoresDiretos) {
      nextSelectAll = true;
      nextSelectedIds = [];
    } else if (tipo === TIPOS.valoresDiretos && Array.isArray(res.valoresDiretos) && res.valoresDiretos.length) {
      nextSelectAll = false;
      nextSelectedIds = res.valoresDiretos.map((item) => (
        item.especialidade?.toString?.() ?? String(item.especialidade)
      ));
    } else if (ids.length) {
      nextSelectAll = false;
      nextSelectedIds = ids;
    } else {
      nextSelectAll = true;
      nextSelectedIds = [];
    }

    setSelectAll(nextSelectAll);
    setSelectedIds(nextSelectedIds);

    if (tipo === TIPOS.valoresDiretos && Array.isArray(res.valoresDiretos)) {
      res.valoresDiretos.forEach((item) => {
        const espId = item.especialidade?.toString?.() ?? String(item.especialidade);
        mapValores[espId] = item.valor;
        if (item.data) {
          mapDatas[espId] = toDatetimeLocalValue(item.data);
        }
      });
      setValoresDiretos(mapValores);
      setDatasVigenciaPorItem(mapDatas);
    } else {
      mapValores = {};
      mapDatas = {};
      setValoresDiretos({});
      setDatasVigenciaPorItem({});
    }

    captureEdicaoDiretaBaseline({
      tipoAlteracao: tipo,
      selectAll: nextSelectAll,
      selectedIds: nextSelectedIds,
      valoresDiretos: mapValores,
      datasVigenciaPorItem: mapDatas,
    });
  }, [resetState, captureEdicaoDiretaBaseline]);

  const loadPending = useCallback(async () => {
    if (!tabelaId) {
      resetState();
      return;
    }

    try {
      const { data: res } = await api.get('/tabelaValorReajustes/ultimo-nao-aplicado', {
        params: { [idParamName]: tabelaId },
      });
      hydrateFromPending(res);
      setReajusteAlert404('');
    } catch (err) {
      resetState();
      const status = err?.response?.status;
      const message = err?.response?.data?.message;
      if (status === 400) {
        notificationRef?.current?.notify({
          message: 'Informe a tabela de valores para buscar reajustes pendentes.',
          color: 'warning',
        });
        return;
      }
      if (status === 404) {
        setReajusteAlert404('Nenhum reajuste pendente encontrado para esta tabela.');
        return;
      }
      if (message) {
        notificationRef?.current?.notify({ message, color: 'danger' });
        return;
      }
      notificationRef?.current?.notify({
        message: 'Algo inesperado aconteceu e não foi possível buscar o reajuste pendente.',
        color: 'danger',
      });
    }
  }, [tabelaId, idParamName, hydrateFromPending, resetState, notificationRef]);

  const selectItemForDirectEdit = useCallback((especialidadeId) => {
    if (!especialidadeId || !hasTabelaId) return;
    setTipoAlteracao(TIPOS.valoresDiretos);
    setSelectAll(false);
    setSelectedIds((prev) => {
      const base = selectAll ? allIds : prev;
      return base.includes(especialidadeId) ? base : [...base, especialidadeId];
    });
    setValoresDiretos((prev) => {
      if (prev[especialidadeId] !== undefined && prev[especialidadeId] !== '') {
        return prev;
      }
      const item = valores.find((v) => v.especialidade?.value === especialidadeId);
      if (!item) return prev;
      return {
        ...prev,
        [especialidadeId]: item.valor,
      };
    });
    setBusca('');
  }, [hasTabelaId, valores, selectAll, allIds]);

  const hasEdicaoDiretaPendente = useCallback(() => {
    if (tipoAlteracao !== TIPOS.valoresDiretos) return false;

    const effectiveIds = selectAll ? allIds : selectedIds;
    if (!effectiveIds.length) return false;

    const temAlteracaoVisivel = effectiveIds.some((espId) => {
      const item = valores.find((v) => v.especialidade?.value === espId);
      const valorAtual = Number(item?.valor);
      const valorNovo = valoresDiretos[espId];
      const dataPreenchida = Boolean(datasVigenciaPorItem[espId]);
      const valorAlterado = valorNovo !== undefined
        && valorNovo !== ''
        && Number.isFinite(Number(valorNovo))
        && Number(valorNovo) !== valorAtual;

      return valorAlterado || dataPreenchida;
    });

    if (!temAlteracaoVisivel) return false;

    // Ignora reajuste já confirmado (hidrato/pendente na API) — só avisa se o usuário mudou o form depois disso
    const currentSnap = buildEdicaoDiretaSnapshot({
      tipoAlteracao,
      selectAll,
      selectedIds,
      valoresDiretos,
      datasVigenciaPorItem,
    });

    return currentSnap !== edicaoDiretaBaselineRef.current;
  }, [
    tipoAlteracao,
    selectAll,
    allIds,
    selectedIds,
    valores,
    valoresDiretos,
    datasVigenciaPorItem,
  ]);

  useImperativeHandle(ref, () => ({
    reset: resetState,
    loadPending,
    selectItemForDirectEdit,
    hasEdicaoDiretaPendente,
  }), [resetState, loadPending, selectItemForDirectEdit, hasEdicaoDiretaPendente]);

  useEffect(() => {
    if (!tabelaId) {
      resetState();
      return;
    }
    loadPending();
  }, [tabelaId, resetState, loadPending]);

  const toggleSelectAll = useCallback((checked) => {
    setSelectAll(checked);
    setSelectedIds([]);
  }, []);

  const toggleItem = useCallback((especialidadeId, checked) => {
    setSelectAll(false);
    setSelectedIds((prev) => {
      if (checked) {
        return prev.includes(especialidadeId) ? prev : [...prev, especialidadeId];
      }
      return prev.filter((id) => id !== especialidadeId);
    });
  }, []);

  const isItemSelected = useCallback((especialidadeId) => {
    if (!hasTabelaId) return false;
    if (selectAll) return true;
    return selectedIds.includes(especialidadeId);
  }, [hasTabelaId, selectAll, selectedIds]);

  const handleConfirmar = useCallback(() => {
    (async () => {
      try {
        setValidationErrors({});

        if (!tabelaId) {
          notificationRef?.current?.notify({
            message: 'Salve a tabela antes de confirmar um reajuste',
            color: 'warning',
          });
          return;
        }

        const effectiveIds = selectAll ? allIds : selectedIds;
        const data = {
          tipoAlteracao,
          [idParamName]: tabelaId,
          especialidadeIds: selectAll ? [] : effectiveIds,
        };

        if (tipoAlteracao === TIPOS.percentual || tipoAlteracao === TIPOS.valorFixo) {
          data.data = datetimeLocalToISO(dataVigencia);
        }

        if (tipoAlteracao === TIPOS.percentual) {
          data.percentual = percentual === '' || percentual === null ? undefined : Number(percentual);
        }
        if (tipoAlteracao === TIPOS.valorFixo) {
          data.valorFixo = valorFixo === '' || valorFixo === null ? undefined : Number(valorFixo);
        }
        if (tipoAlteracao === TIPOS.valoresDiretos) {
          data.valoresDiretos = effectiveIds.map((espId) => ({
            especialidade: espId,
            valor: Number(valoresDiretos[espId]),
            data: datetimeLocalToISO(datasVigenciaPorItem[espId]),
          }));
          const datas = data.valoresDiretos
            .map((item) => item.data)
            .filter(Boolean)
            .map((d) => new Date(d).getTime());
          if (datas.length) {
            data.data = new Date(Math.min(...datas)).toISOString();
          }
        }

        const schemaFields = {
          tipoAlteracao: Yup.string().required(),
        };

        if (tipoAlteracao === TIPOS.percentual || tipoAlteracao === TIPOS.valorFixo) {
          schemaFields.data = Yup.date()
            .typeError('Data/hora inválida')
            .required('Informe a data/hora de vigência');
        }

        if (tipoAlteracao === TIPOS.percentual) {
          schemaFields.percentual = Yup.number()
            .typeError('Informe o percentual de reajuste')
            .required('Informe o percentual de reajuste')
            .test('not-zero', 'O percentual deve ser diferente de zero', (v) => v !== 0);
        }
        if (tipoAlteracao === TIPOS.valorFixo) {
          schemaFields.valorFixo = Yup.number()
            .typeError('Informe o valor fixo')
            .required('Informe o valor fixo')
            .test('not-zero', 'O valor fixo deve ser diferente de zero', (v) => v !== 0);
        }

        const schema = Yup.object().shape(schemaFields);
        await schema.validate(data, { abortEarly: false });

        if (!effectiveIds.length) {
          notificationRef?.current?.notify({
            message: 'Selecione ao menos um tipo de atendimento',
            color: 'warning',
          });
          return;
        }

        if (tipoAlteracao === TIPOS.valoresDiretos) {
          const missingValor = effectiveIds.some(
            (espId) => valoresDiretos[espId] === undefined
              || valoresDiretos[espId] === ''
              || !Number.isFinite(Number(valoresDiretos[espId])),
          );
          if (missingValor) {
            notificationRef?.current?.notify({
              message: 'Informe o novo valor de todos os itens selecionados',
              color: 'warning',
            });
            return;
          }
          const missingData = effectiveIds.some(
            (espId) => !datasVigenciaPorItem[espId] || !datetimeLocalToISO(datasVigenciaPorItem[espId]),
          );
          if (missingData) {
            notificationRef?.current?.notify({
              message: 'Informe a data/hora de vigência de todos os itens selecionados',
              color: 'warning',
            });
            return;
          }
        }

        setConfirming(true);
        if (setLoading) {
          setLoading({
            loading: true,
            message: 'Aguarde... confirmando o reajuste... ',
          });
        }

        const res = await api.post('/tabelaValorReajustes', data);
        const reajuste = res?.data?.tabelaValorReajuste;
        const aplicado = reajuste?.aplicado;
        const teveAplicacaoParcial = Boolean(res?.data?.tabelaValorUpdated?.valores?.length)
          && (aplicado || (reajuste?.itensAlterados || []).length > 0);

        if (teveAplicacaoParcial && res?.data?.tabelaValorUpdated?.valores?.length) {
          onApplied(res.data.tabelaValorUpdated);
        }

        if (aplicado) {
          resetState();
          setReajusteAlert404('Nenhum reajuste pendente encontrado para esta tabela.');
        } else {
          hydrateFromPending(reajuste);
          setReajusteAlert404('');
        }

        notificationRef?.current?.notify({
          message: aplicado
            ? 'Reajuste confirmado com sucesso'
            : 'Reajuste agendado com sucesso para a data/hora de vigência',
          color: 'success',
        });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          notificationRef?.current?.notify({
            message: 'Por favor, preencha todos os dados necessários para confirmar o reajuste',
            color: 'warning',
          });
          const ve = {};
          err.inner.forEach((e) => {
            ve[e.path] = e.message;
          });
          setValidationErrors(ve);
          return;
        }

        const message = err?.response?.data?.message;
        if (message) {
          notificationRef?.current?.notify({ message, color: 'danger' });
          return;
        }

        notificationRef?.current?.notify({
          message: 'Algo inesperado aconteceu e não foi possível confirmar o reajuste',
          color: 'danger',
        });
      } finally {
        setConfirming(false);
        if (setLoading) setLoading(false);
      }
    })();
  }, [
    selectAll,
    allIds,
    selectedIds,
    tipoAlteracao,
    dataVigencia,
    datasVigenciaPorItem,
    idParamName,
    tabelaId,
    percentual,
    valorFixo,
    valoresDiretos,
    notificationRef,
    setLoading,
    onApplied,
    resetState,
    hydrateFromPending,
  ]);

  const isEdicaoDireta = tipoAlteracao === TIPOS.valoresDiretos;

  return (
    <Row className="mb-3">
      {hasTabelaId ? (
        <>
          <Col xs={12}>
            <h5 className="mb-3">Reajuste de valores</h5>
          </Col>

          <Col xs={12} md={4} className="mb-2">
            <FormGroup>
              <Label>Tipo de alteração</Label>
              <Input
                type="select"
                value={tipoAlteracao}
                disabled={disabled || confirming}
                onChange={({ target: { value } }) => setTipoAlteracao(value)}
              >
                <option value={TIPOS.percentual}>Percentual</option>
                <option value={TIPOS.valorFixo}>Valor fixo (soma/subtrai)</option>
                <option value={TIPOS.valoresDiretos}>Edição direta</option>
              </Input>
            </FormGroup>
          </Col>

          <Col xs={12} md={isEdicaoDireta ? 6 : 3} className="mb-2">
            {tipoAlteracao === TIPOS.percentual && (
              <FormGroup>
                <Label>Percentual (%)</Label>
                <Input
                  type="number"
                  placeholder="Ex: 5"
                  value={percentual ?? ''}
                  disabled={disabled || confirming}
                  onChange={({ target: { value } }) => setPercentual(value === '' ? null : value)}
                  invalid={!!validationErrors.percentual}
                />
                <FormFeedback>{validationErrors.percentual}</FormFeedback>
              </FormGroup>
            )}
            {tipoAlteracao === TIPOS.valorFixo && (
              <FormGroup>
                <Label>Valor fixo</Label>
                <NumericFormat
                  customInput={Input}
                  placeholder="Ex: 10,00"
                  decimalSeparator=","
                  thousandSeparator="."
                  decimalScale={2}
                  prefix="R$ "
                  value={valorFixo ?? ''}
                  disabled={disabled || confirming}
                  fixedDecimalScale
                  invalid={!!validationErrors.valorFixo}
                  onValueChange={({ floatValue }) => setValorFixo(floatValue ?? null)}
                />
                <FormFeedback>{validationErrors.valorFixo}</FormFeedback>
              </FormGroup>
            )}
            {isEdicaoDireta && (
              <FormGroup>
                <Label>&nbsp;</Label>
                <Alert color="secondary" className="mb-0 py-2">
                  Informe o novo valor e a data/hora de vigência em cada item selecionado abaixo.
                </Alert>
              </FormGroup>
            )}
          </Col>

          {!isEdicaoDireta && (
            <Col xs={12} md={3} className="mb-2">
              <FormGroup>
                <Label>Data/hora de vigência</Label>
                <Input
                  type="datetime-local"
                  value={dataVigencia || ''}
                  disabled={disabled || confirming}
                  onChange={({ target: { value } }) => setDataVigencia(value || '')}
                  invalid={!!validationErrors.data}
                />
                <FormFeedback>{validationErrors.data}</FormFeedback>
              </FormGroup>
            </Col>
          )}

          <Col xs={12} md={2} className="mb-2 d-flex align-items-end">
            <FormGroup className="w-100 mb-0">
              <UncontrolledTooltip target="confirmar-reajuste-unificado">
                Confirmar reajuste
              </UncontrolledTooltip>
              <Button
                id="confirmar-reajuste-unificado"
                color="success"
                className="text-white"
                disabled={disabled || confirming || !valores.length}
                onClick={handleConfirmar}
                block
              >
                <i className="fa-solid fa-circle-plus text-white mr-2" />
                Confirmar
              </Button>
            </FormGroup>
          </Col>

          {reajusteAlert404 ? (
            <Col xs={12} className="mb-2">
              <Alert color="info" className="mb-0">
                {reajusteAlert404}
              </Alert>
            </Col>
          ) : null}

          <Col xs={12} md={4} className="mb-2">
            <FormGroup>
              <Label>Buscar tipo de atendimento</Label>
              <Input
                type="text"
                placeholder="Buscar por nome"
                value={busca}
                disabled={disabled || confirming}
                onChange={({ target: { value } }) => setBusca(value)}
              />
            </FormGroup>
          </Col>

          <Col xs={12} md={4} className="mb-2 d-flex align-items-end">
            <FormGroup check className="mb-3">
              <Input
                id="reajuste-select-all"
                type="checkbox"
                checked={selectAll}
                disabled={disabled || confirming || !valores.length}
                onChange={({ target: { checked } }) => toggleSelectAll(checked)}
              />
              <Label check for="reajuste-select-all">
                Todos os tipos de atendimento
              </Label>
            </FormGroup>
          </Col>
        </>
      ) : (
        <Col xs={12}>
          <h5 className="mb-3">Tipos de atendimento</h5>
        </Col>
      )}

      <Col xs={12}>
        <div
          style={{
            border: '1px solid #dee2e6',
            borderRadius: 4,
            padding: '0.75rem',
          }}
        >
          {!filteredValores.length ? (
            <p className="text-muted mb-0 text-center">Cadastre os valores</p>
          ) : (
            filteredValores.map((v, index) => {
              const espId = v.especialidade?.value;
              const selected = isItemSelected(espId);
              const projected = hasTabelaId
                ? computeProjectedValor(v.valor, espId, previewConfig)
                : null;
              const showDiretoInputs = hasTabelaId && isEdicaoDireta && selected;
              let nomeColMd = 6;
              if (showDiretoInputs) {
                nomeColMd = 3;
              } else if (hasTabelaId) {
                nomeColMd = 5;
              }

              return (
                <Row key={espId || index} className="align-items-center mb-2">
                  <Col
                    xs={12}
                    md={nomeColMd}
                    className="d-flex align-items-center"
                  >
                    {hasTabelaId ? (
                      <FormGroup check className="mb-0">
                        <Input
                          id={`reajuste-item-${espId}`}
                          type="checkbox"
                          checked={selected}
                          disabled={disabled || confirming || selectAll}
                          onChange={({ target: { checked } }) => toggleItem(espId, checked)}
                        />
                        <Label check for={`reajuste-item-${espId}`} className="mb-0">
                          {v.especialidade?.label}
                        </Label>
                      </FormGroup>
                    ) : (
                      <span>{v.especialidade?.label}</span>
                    )}
                  </Col>

                  <Col xs={6} md={showDiretoInputs ? 2 : 4}>
                    <MoneyFormatter value={parseFloat(v.valor)} />
                    {projected !== null && (
                      <span className="text-muted ml-2">
                        &rarr;
                        {' '}
                        <MoneyFormatter value={projected} />
                      </span>
                    )}
                  </Col>

                  {showDiretoInputs && (
                    <>
                      <Col xs={6} md={2} className="mb-2 mb-md-0">
                        <NumericFormat
                          customInput={Input}
                          placeholder="Novo valor"
                          decimalSeparator=","
                          thousandSeparator="."
                          decimalScale={2}
                          prefix="R$ "
                          value={valoresDiretos[espId] ?? ''}
                          disabled={disabled || confirming}
                          fixedDecimalScale
                          onValueChange={({ floatValue }) => {
                            setValoresDiretos((prev) => ({
                              ...prev,
                              [espId]: floatValue,
                            }));
                          }}
                        />
                      </Col>
                      <Col xs={8} md={3} className="mb-2 mb-md-0">
                        <Input
                          type="datetime-local"
                          value={datasVigenciaPorItem[espId] || ''}
                          disabled={disabled || confirming}
                          onChange={({ target: { value } }) => {
                            setDatasVigenciaPorItem((prev) => ({
                              ...prev,
                              [espId]: value || '',
                            }));
                          }}
                        />
                      </Col>
                    </>
                  )}

                  <Col
                    xs={showDiretoInputs ? 4 : 6}
                    md={showDiretoInputs ? 2 : 3}
                    className="d-flex justify-content-end"
                  >
                    {hasTabelaId && (
                      <>
                        <UncontrolledTooltip target={`edit-button-${espId}`}>
                          Reajustar este tipo de atendimento
                        </UncontrolledTooltip>
                        <Button
                          id={`edit-button-${espId}`}
                          color="info"
                          className="mr-2"
                          disabled={disabled || confirming}
                          onClick={() => selectItemForDirectEdit(espId)}
                        >
                          <i className="fa-solid fa-edit text-white" />
                        </Button>
                      </>
                    )}
                    <UncontrolledTooltip target={`delete-button-${espId}`}>
                      Excluir
                    </UncontrolledTooltip>
                    <Button
                      id={`delete-button-${espId}`}
                      color="danger"
                      disabled={disabled || confirming}
                      onClick={() => onRemoveValor(v.especialidade)}
                    >
                      <i className="fa-solid fa-trash-can text-white" />
                    </Button>
                  </Col>
                </Row>
              );
            })
          )}
        </div>
      </Col>
    </Row>
  );
}

export default forwardRef(TabelaValorReajustePanel);
